<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useCrmStore } from 'dashboard/stores/crm';
import CrmOpportunityCard from './CrmOpportunityCard.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  opportunities: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['opportunity-click', 'add-opportunity']);

const { t } = useI18n();
const crmStore = useCrmStore();

const totalValueDisplay = computed(() => {
  const totalCents = props.opportunities.reduce(
    (sum, opportunity) => sum + (opportunity.value_cents || 0),
    0
  );
  if (totalCents === 0) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(totalCents / 100);
});

const handleChange = ({ added }) => {
  if (added) {
    crmStore.moveOpportunity(added.element.id, props.stage.id);
  }
};
</script>

<template>
  <div class="flex flex-col w-[280px] h-full flex-shrink-0">
    <div class="flex items-center gap-2 px-3 py-2 bg-n-slate-3 rounded-t">
      <span
        class="inline-block w-3 h-3 rounded-full flex-shrink-0"
        :style="{ backgroundColor: stage.color }"
      />
      <span class="text-sm font-semibold text-n-slate-12 truncate flex-1">
        {{ stage.name }}
      </span>
      <span
        class="text-xs font-medium text-n-slate-11 bg-n-slate-4 rounded-full px-1.5 py-0.5 leading-none"
      >
        {{ opportunities.length }}
      </span>
      <button
        class="text-n-slate-10 hover:text-n-slate-12 transition-colors duration-150 text-lg leading-none ml-1"
        :title="t('CRM.KANBAN.ADD_OPPORTUNITY')"
        @click="emit('add-opportunity', stage.id)"
      >
        {{ '+' }}
      </button>
    </div>
    <p
      v-if="totalValueDisplay"
      class="text-xs text-n-slate-10 px-3 py-1 bg-n-slate-3 border-t border-n-slate-4"
    >
      {{ totalValueDisplay }}
    </p>

    <Draggable
      :list="opportunities"
      group="opportunities"
      item-key="id"
      :animation="200"
      ghost-class="opacity-50"
      class="flex-1 overflow-y-auto p-2 flex flex-col gap-2 bg-n-slate-2 rounded-b"
      @change="handleChange"
    >
      <template #item="{ element }">
        <CrmOpportunityCard
          :opportunity="element"
          @click="emit('opportunity-click', element.id)"
        />
      </template>
      <template #footer>
        <div
          v-if="opportunities.length === 0"
          class="flex items-center justify-center py-6 text-xs text-n-slate-10"
        >
          {{ t('CRM.KANBAN.EMPTY_STATE') }}
        </div>
      </template>
    </Draggable>
  </div>
</template>
